import clsx from "clsx";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { isValidSignature } from "../common";
import { SignatureItem } from "../types";
import SignatureOverlay from "./SignatureOverlay";

interface IframeProps extends ComponentPropsWithoutRef<"iframe"> {
  hasSignature?: boolean;
  onSignature?: (
    pdfDocumentViewer: any,
    signatureVariable: string,
    signatureData: string
  ) => void;
  signatureVariables?: string[];
  signedVariables?: string[];
  onAllSignaturesCompleted?: () => void; // Callback when all signatures are signed
  storageId?: string; // If exists, document is already signed (USB signed)
}

const MAX_SCALE = 3.0;
const MIN_SCALE = 0.5;
const STEP = 0.1;

const FilePreview = forwardRef<HTMLIFrameElement, IframeProps>(
  (
    {
      src,
      onSignature,
      hasSignature,
      signatureVariables = [],
      signedVariables = [],
      onAllSignaturesCompleted,
      storageId,
    }: IframeProps,
    ref
  ) => {
    const [signaturePositions, setSignaturePositions] = useState<
      SignatureItem[]
    >([]);
    const [numPages, setNumPages] = useState(0);
    const [pdfViewport, setPdfViewport] = useState({ width: 0, height: 0 });
    const [scale, setScale] = useState(1);

    const containerRef = useRef<HTMLDivElement>(null);
    const pdfDocumentRef = useRef<any>(null);
    const hasTriggeredUsbRef = useRef(false); // Track if USB signing was already triggered

    useEffect(() => {
      const node = containerRef.current;

      if (node) {
        node.addEventListener("wheel", onMouseWheel, { passive: false });
      }

      return () => {
        node && node.removeEventListener("wheel", onMouseWheel);
      };
    }, []);
    const zoomIn = () =>
      setScale((s) => Math.min(MAX_SCALE, +(s + STEP).toFixed(2)));
    const zoomOut = () =>
      setScale((s) => Math.max(MIN_SCALE, +(s - STEP).toFixed(2)));

    const onMouseWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();

        if (e.deltaY < 0) {
          zoomIn();
        } else if (e.deltaY > 0) {
          zoomOut();
        }
      }
    };

    const onDocumentLoadSuccess = async (pdf: pdfjs.PDFDocumentProxy) => {
      setNumPages(pdf.numPages);
      const firstPage = await pdf.getPage(1);
      const viewport = firstPage.getViewport({ scale: 1 });
      setPdfViewport({ width: viewport.width, height: viewport.height });

      console.log('=== FilePreview Document Load ===');
      console.log('hasSignature:', hasSignature);
      console.log('storageId:', storageId);
      console.log('signatureVariables:', signatureVariables);
      console.log('signedVariables:', signedVariables);

      if (!hasSignature) return;
      pdfDocumentRef.current = pdf;

      // If signatureVariables is null/undefined or empty array, don't show signature positions
      if (!signatureVariables || !Array.isArray(signatureVariables)) {
        console.log(' signatureVariables is null/undefined - hiding signature positions');
        setSignaturePositions([]);
        return;
      }

      // Calculate remaining signature keys (variables not yet signed)
      const signatures: SignatureItem[] = [];
      const signKeys = signatureVariables.filter(
        (key) => !(signedVariables ?? []).includes(key)
      );

      console.log('📝 Signature keys to detect:', signKeys);

      // Only hide signature positions if there are NO remaining keys to sign
      if (!signKeys.length) {
        console.log(' No signature keys remaining - hiding signature positions');
        setSignaturePositions([]);
        return;
      }

      // If there are still keys to sign, detect and show signature positions
      console.log(`🔍 Detecting ${signKeys.length} signature position(s)...`);

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        const pdfTextContent = await page.getTextContent();

        pdfTextContent.items.forEach((item: any) => {
          const str = item.str;

          if (isValidSignature(signKeys, str)) {
            const [, , , , x, y] = item.transform;
            const width = item.width;
            const height = item.height;

            // Giữ lại value từ state cũ nếu variable trùng khớp
            const existingSignature = signaturePositions.find(
              (sig) => sig.variable === str
            );

            signatures.push({
              variable: str,
              x,
              y: viewport.height - y,
              width,
              height,
              page: pageNumber,
              value: existingSignature?.value, // Preserve existing value
            });
          }
        });
      }
      setSignaturePositions(signatures);
    };

    // Check if all signatures are completed
    useEffect(() => {
      if (!hasSignature || !signaturePositions.length) return;
      if (hasTriggeredUsbRef.current) return; // Already triggered

      // Count signed and unsigned signatures
      const signedCount = signaturePositions.filter((sig) => !!sig.value).length;
      const totalCount = signaturePositions.length;
      const allSigned = signedCount === totalCount;

      if (allSigned) {
        console.log("All signatures completed! Triggering USB signing...");
        hasTriggeredUsbRef.current = true;
        onAllSignaturesCompleted?.();
      } else {
        console.log(
          `⏳ Waiting for ${totalCount - signedCount} more signature(s)...`
        );
      }
    }, [signaturePositions, hasSignature, onAllSignaturesCompleted]);

    // Reset trigger flag when src changes (new document)
    useEffect(() => {
      hasTriggeredUsbRef.current = false;
    }, [src]);

    const onSign = (signatureVariable: string, signatureValue: string) => {
      setSignaturePositions(
        signaturePositions.map((signature) => {
          if (signature.variable === signatureVariable) {
            return {
              ...signature,
              value: signatureValue,
            };
          }
          return signature;
        })
      );
      onSignature?.(pdfDocumentRef.current, signatureVariable, signatureValue);
    };

    return (
      <div ref={containerRef} className="h-100 overflow-hidden">
        <iframe
          ref={ref}
          title="print-iframe"
          src={src}
          className="spaces w-0vw h-0 p-0 m-0 border-0 d-flex"
        />
        <Document
          file={src}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={console.error}
          className="h-100 overflow-y-auto bg-light-dark d-flex flex-column align-items-center"
          loading="Đang tải dữ liệu..."
          noData="Không có dữ liệu."
        >
          {Array.from({ length: numPages }, (_, index) => {
            const currentPage = index + 1;
            return (
              <div
                key={index}
                className={clsx("spaces mb-16", {
                  "mt-12": index === 0,
                })}
              >
                <Page
                  key={index}
                  pageNumber={currentPage}
                  scale={scale}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  loading="Đang tải dữ liệu..."
                  noData="Không có dữ liệu."
                  className={clsx("bg-light-dark", {
                    "d-flex justify-content-center align-items-center": !hasSignature,
                  })}
                >
                  {!!signaturePositions.length &&
                    signaturePositions
                      .filter((position) => position.page === currentPage)
                      .map((position, signIndex) => {
                        const left = position.x * scale;
                        const top =
                          position.y * scale - position.height * scale;
                        const width = position.width * scale;
                        const height = `calc(${scale} * ${
                          position.height * 5
                        }px)`;

                        return (
                          <div
                            key={`${position.variable}-page-${index}-${signIndex}`}
                            style={{
                              position: "absolute",
                              pointerEvents: "auto",
                              left: `${left}px`,
                              top: `${top}px`,
                              width: `${width}px`,
                              height: `${height}`,
                            }}
                          >
                            <SignatureOverlay
                              signatureItem={position}
                              onSignature={(value) => {
                                onSign(position.variable, value);
                              }}
                              documentScale={scale}
                            />
                          </div>
                        );
                      })}
                </Page>
              </div>
            );
          })}
        </Document>
      </div>
    );
  }
);

export default FilePreview;
